import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar.jsx";
import Footer from "../components/sub-comp/MainFooter.jsx";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import ProfessionalCourseApplicationForm from "../components/Courses/ProfessionalCourses/ProfessionalCourseApplicationForm";
import { FaCertificate, FaGlobe, FaShieldAlt, FaNetworkWired, FaClock, FaArrowRight } from "react-icons/fa";
import cmsApi from "../services/cmsApi";

const scrollToApplicationForm = () => {
  const element = document.getElementById("application-form");
  if (!element) return;
  const navbar = document.querySelector("header.sticky");
  const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 140;
  const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
  window.scrollTo({ top: elementTop - navbarHeight - 16, behavior: "smooth" });
};

const ProfessionalCourses = () => {
  const [heroContent, setHeroContent] = useState({
    title: "Professional Courses",
    subtitle: "Industry-recognized certifications and training programs to advance your career in technology"
  });
  const [professionalCourses, setProfessionalCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // New sections state
  const [pathSection, setPathSection] = useState({
    title: "PROFESSIONALLY CERTIFIED. GLOBALLY RECOGNISED.",
    description: "At JRIIT, our Professional Courses connect you directly to the world's most trusted IT certification bodies. In partnership with CompTIA, Cisco Networking Academy, and EC-Council, we offer structured, hands-on training programs that go beyond theory — giving you internationally recognised credentials that employers across Tanzania and the globe actively seek. Whether you are a graduate looking to specialise or a working professional ready to level up, these programs are your fastest path to standing out in the digital economy."
  });
  const [whyChooseSection, setWhyChooseSection] = useState({
    title: "Why Choose Professional Certifications?",
    description: "Professional certifications validate your expertise and open doors to new career opportunities."
  });
  const [benefitCards, setBenefitCards] = useState([
    {
      icon: 'FaGlobe',
      title: 'Global Recognition',
      description: 'Industry-standard certifications recognized worldwide by employers and organizations.'
    },
    {
      icon: 'FaNetworkWired',
      title: 'Career Advancement',
      description: 'Boost your career prospects with specialized skills and knowledge in high-demand areas.'
    },
    {
      icon: 'FaShieldAlt',
      title: 'Expert Training',
      description: 'Learn from industry experts with hands-on experience and real-world scenarios.'
    }
  ]);

  useEffect(() => {
    // Only fetch on client side to avoid hydration issues
    if (typeof window === 'undefined') return;

    const fetchContent = async () => {
      try {
        setLoading(true);
        
        // Fetch header component for Professional Course page
        const headerResult = await cmsApi.getPageComponent('professional-course', 'header');
        
        if (headerResult.success && headerResult.components && headerResult.components.length > 0) {
          const header = headerResult.components[0];
          setHeroContent({
            title: header.title && header.subtitle 
              ? `${header.title} ${header.subtitle}` 
              : header.title || "Professional Courses",
            subtitle: header.content || "Industry-recognized certifications and training programs to advance your career in technology"
          });
        }

        // Fetch Professional Course programs from CMS
        const programsResult = await cmsApi.getProgramsByDepartment('professional-course');
        
        // Maps DB slugs to the static route segments defined in App.js
          const DETAIL_ROUTE = {
            'comptia-certification': 'comptia',
            'cisco-networking-academy': 'cisco-networking-academy',
            'ec-council-certification': 'ec-council',
          };

        if (programsResult.success && programsResult.programs) {
          const formattedCourses = programsResult.programs
            .filter(program => program.isActive)
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(program => {
              // Parse keyCertifications from JSON
              let certifications = [];
              if (program.keyCertifications) {
                try {
                  certifications = JSON.parse(program.keyCertifications);
                } catch (e) {
                  console.error('Error parsing keyCertifications:', e);
                }
              }

              return {
                id: program.slug,
                detailPath: `/courses/professional-courses/${DETAIL_ROUTE[program.slug] || program.slug}`,
                title: program.name,
                description: program.shortDescription || '',
                image: program.cardImage || program.featuredImage || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=300&fit=crop',
                features: Array.isArray(certifications)
                  ? certifications.map((c) => {
                      const dash = c.indexOf(' — ');
                      return dash === -1 ? c : c.slice(0, dash);
                    })
                  : [],
                duration: program.duration || 'N/A',
                level: program.level || 'All Levels',
              };
            });
          
          setProfessionalCourses(formattedCourses);
        }

        // Fetch "Choose Your Path" section
        const pathResult = await cmsApi.getPageComponent('professional-course', 'choose-path');
        if (pathResult.success && pathResult.components && pathResult.components.length > 0) {
          const path = pathResult.components[0];
          setPathSection({
            title: path.title || "Choose Your Professional Path",
            description: path.content || "Our professional courses are designed in partnership with industry leaders to provide you with the most current and relevant skills."
          });
        }

        // Fetch "Why Choose" section
        const whyResult = await cmsApi.getPageComponent('professional-course', 'why-choose');
        if (whyResult.success && whyResult.components && whyResult.components.length > 0) {
          const why = whyResult.components[0];
          setWhyChooseSection({
            title: why.title || "Why Choose Professional Certifications?",
            description: why.content || "Professional certifications validate your expertise and open doors to new career opportunities."
          });
          
          // Parse benefit cards
          if (why.settings) {
            try {
              const settings = typeof why.settings === 'string' ? JSON.parse(why.settings) : why.settings;
              if (settings.benefitCards && Array.isArray(settings.benefitCards)) {
                setBenefitCards(settings.benefitCards);
              }
            } catch (e) {
              console.error('Error parsing benefit cards:', e);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching Professional Course content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="mid-desktop-page">
      
      {/* Hero Section - Now Dynamic */}
      <div className="bg-gradient-to-r from-custom-pink to-gold py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {heroContent.title}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-8">
            {heroContent.subtitle}
          </p>
          <button
            type="button"
            onClick={scrollToApplicationForm}
            className="inline-flex items-center gap-3 bg-gold text-white px-8 py-4 rounded-full text-lg font-semibold shadow-lg hover:bg-yellow-500 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
          >
            Start Application
            <FaArrowRight />
          </button>
        </div>
      </div>

      {/* Courses Section */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              {pathSection.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {pathSection.description}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-custom-pink"></div>
            </div>
          ) : professionalCourses.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600">No professional courses available at this time.</p>
            </div>
          ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {professionalCourses.map((course) => (
              <div key={course.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col">
                <div className="relative">
                  <img
                    src={course.image}
                    alt={course.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="bg-custom-pink text-white px-3 py-1 rounded-full text-sm font-semibold">
                      Professional
                    </span>
                  </div>
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center mb-3">
                    <FaCertificate className="text-gold mr-2" />
                    <h3 className="text-2xl font-bold text-gray-800">{course.title}</h3>
                  </div>

                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {course.description}
                  </p>

                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2">Key Certifications:</h4>
                    <ul className="space-y-1">
                      {course.features.map((feature, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-600">
                          <span className="w-2 h-2 bg-gold rounded-full mr-2"></span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex justify-between items-center mb-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <FaClock className="mr-1" />
                      {course.duration}
                    </span>
                    <span className="flex items-center">
                      <FaShieldAlt className="mr-1" />
                      {course.level}
                    </span>
                  </div>

                  <div className="mt-auto">
                    <Link
                      to={course.detailPath}
                      className="w-full bg-custom-pink hover:bg-gold text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center"
                    >
                      Learn More
                      <FaArrowRight className="ml-2" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">
              {whyChooseSection.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {whyChooseSection.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
            {benefitCards.map((card, index) => {
              // Map icon names to actual icon components
              const IconComponent = {
                'FaGlobe': FaGlobe,
                'FaNetworkWired': FaNetworkWired,
                'FaShieldAlt': FaShieldAlt,
                'FaCertificate': FaCertificate,
                'FaStar': FaCertificate, // Fallback
                'FaTrophy': FaCertificate, // Fallback
                'FaUsers': FaCertificate, // Fallback
                'FaLightbulb': FaCertificate // Fallback
              }[card.icon] || FaCertificate;

              return (
                <div key={index} className="text-center">
                  <div className="bg-custom-pink text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="text-2xl" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{card.title}</h3>
                  <p className="text-gray-600">{card.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Application Form */}
      <ProfessionalCourseApplicationForm />

      </div>

      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default ProfessionalCourses;
